/**
 * Reminder types — mirrors the taxhive-notification-service API contract
 * (`ReminderResponse` / `ReminderList` schemas), camelCased for the UI.
 *
 * The HTTP mappers do the snake⇄camel conversion; field names follow the API
 * SCHEMA (`dueDate`, `entityType`, `notifyChannels`).
 */

// ============================================================================
// ENUM-LIKE UNIONS (match the backend's ReminderStatus / Type / Priority)
// ============================================================================

/** Reminder lifecycle status. Matches `ReminderStatus`. */
export type ReminderStatus =
  | 'pending'
  | 'sent'
  | 'completed'
  | 'dismissed'
  | 'snoozed'
  | 'cancelled'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {})

/** Reminder type. The backend enum has many values; the UI keeps the union open. */
export type ReminderType =
  | 'custom'
  | 'filing_due'
  | 'payment_due'
  | 'document_request'
  | 'appointment'
  | 'follow_up'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {})

/** Priority level. Matches `ReminderPriority`. */
export type ReminderPriority = 'low' | 'medium' | 'high' | 'urgent'

/**
 * Notification channel. Matches the backend's allowed `notify_channels` values.
 */
export type ReminderChannel = 'in_app' | 'email' | 'sms' | 'push' | 'whatsapp'

/**
 * Linked-entity type. The backend stores a free-form string; the UI passes
 * `individual_customer` for the customer Reminders tab.
 */
export type ReminderEntityType =
  | 'individual_customer'
  | 'business_customer'
  | 'tax_assessment'
  | 'customer'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {})

// ============================================================================
// MAIN REMINDER INTERFACE
// ============================================================================

export interface Reminder {
  id: string

  // Entity reference
  entityType: ReminderEntityType
  entityId: string

  // Details
  title: string
  description?: string
  reminderType: ReminderType
  priority: ReminderPriority

  // Timing
  dueDate: string // ISO datetime
  remindAt?: string
  remindBeforeMinutes?: number

  // Status
  status: ReminderStatus
  statusChangedAt?: string

  // Completion
  completedAt?: string
  completedByName?: string
  completionNotes?: string

  // Dismissal
  dismissedAt?: string
  dismissedByName?: string
  dismissedReason?: string

  // Snooze
  snoozedUntil?: string
  snoozeCount?: number

  // Assignment
  assignedToUserId?: string
  assignedToName?: string

  // Recurrence
  recurrence?: ReminderRecurrence
  isRecurring?: boolean
  recurrenceFrequency?: string

  // Notification settings
  notifyChannels: ReminderChannel[]

  // Calculated (server-provided)
  isOverdue?: boolean
  hoursUntilDue?: number

  // Audit
  createdByName?: string
  createdAt: string
  updatedAt: string
}

// ============================================================================
// INPUTS
// ============================================================================

export type ReminderRecurrence = 'none' | 'monthly' | 'quarterly' | 'annual'

export const REMINDER_RECURRENCE_LABEL: Record<ReminderRecurrence, string> = {
  none: 'One-time (No recurrence)',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
}

export const REMINDER_RECURRENCE_OPTIONS: ReminderRecurrence[] = [
  'none',
  'monthly',
  'quarterly',
  'annual',
]

/** Create payload for a new reminder linked to an entity. */
export interface CreateReminderInput {
  entityType: ReminderEntityType
  entityId: string
  title: string
  dueDate: string // ISO datetime
  description?: string
  reminderType?: ReminderType
  priority?: ReminderPriority
  notifyChannels?: ReminderChannel[]
  assignedToUserId?: string
  assignedToName?: string
  recurrence?: ReminderRecurrence
  remindBeforeMinutes?: number
}

/**
 * Partial update. `status` flips a reminder to completed/dismissed; the http
 * service routes those to the dedicated complete/dismiss endpoints.
 * `snoozeMinutes` triggers the snooze endpoint.
 */
export interface UpdateReminderInput {
  title?: string
  description?: string
  priority?: ReminderPriority
  dueDate?: string
  status?: ReminderStatus
  notifyChannels?: ReminderChannel[]
  /** Convenience field — when set, the http service calls the snooze endpoint. */
  snoozeMinutes?: number
  /** Optional note used when completing/dismissing. */
  notes?: string
}

// ============================================================================
// DISPLAY LABELS
// ============================================================================

export const REMINDER_PRIORITY_LABEL: Record<ReminderPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

export const REMINDER_CHANNEL_LABEL: Record<ReminderChannel, string> = {
  in_app: 'In-app',
  email: 'Email',
  sms: 'SMS',
  push: 'Push',
  whatsapp: 'WhatsApp',
}

export const REMINDER_CHANNEL_OPTIONS: ReminderChannel[] = [
  'in_app',
  'email',
  'sms',
  'whatsapp',
]

/** True when a reminder is still actionable (not completed/dismissed/cancelled). */
export function isOpenReminder(r: Reminder): boolean {
  return r.status !== 'completed' && r.status !== 'dismissed' && r.status !== 'cancelled'
}
